// Attaches the active Supabase session token to every request the app
// makes to the KrewUp backend, without touching the ~80 existing call
// sites that already call fetch(getApiUrl(...)).
//
// Phase 1 of the security roadmap: the backend is starting to require
// Authorization on its highest-risk routes. This must be imported exactly
// once, as early as possible (see src/index.tsx), before any component
// has a chance to call fetch().
import { isAuthRetryableFetchError, type Session } from '@supabase/supabase-js';
import { supabase } from './supabase';
import { getSession, signOutLocally } from './auth-session';
import { API_BASE_URL } from './api-config';
import { softenPermissionDenied } from './permission-errors';
import { COMPANY_HEADER, getSelectedCompanyId } from './active-company';
import { loginPathWithNext } from './return-path';

const nativeFetch = window.fetch.bind(window);

function resolveUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.toString();
  return input.url;
}

// The backend's requireAuth (middleware/auth.js) answers 401 for ANY failure
// validating the token: an expired access token (the common case -- the
// cached token outlives its 1h lifetime while the tab is in the background,
// and the notifications poll then sends it), but also a transient failure
// reaching Supabase Auth. So a 401 is NOT proof the session is dead.
// Found live (2026-10-15, HAR): the poll's 401 triggered a global signOut
// even though the refresh token was valid (refresh 200, then logout).
//
// Now: on 401, try ONE shared refresh and retry the request once. Only when
// the refresh itself is rejected as non-retryable (refresh token revoked/
// invalid, user gone -- the 2026-09-11 deleted-account case) is the session
// truly dead and the user sent to /login. A network failure while refreshing
// leaves the session alone.
let forcingSignOut = false;
let refreshInFlight: Promise<Session | null | 'dead'> | null = null;

async function forceSignOutToLogin() {
  if (forcingSignOut || window.location.pathname === '/login') return;
  forcingSignOut = true;
  await signOutLocally();
  // a document opened from a link comes back to it after signing in again
  window.location.href = loginPathWithNext(`${window.location.pathname}${window.location.search}`);
}

/** One refresh shared by every request that 401s at the same time.
 *  Resolves the new session; null = couldn't tell (network); 'dead' = the
 *  refresh was rejected for good. */
function refreshSessionOnce(): Promise<Session | null | 'dead'> {
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      try {
        const { data, error } = await supabase.auth.refreshSession();
        if (error) return isAuthRetryableFetchError(error) ? null : 'dead';
        return data.session ?? 'dead';
      } catch {
        return null;
      } finally {
        refreshInFlight = null;
      }
    })();
  }
  return refreshInFlight;
}

window.fetch = async (input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> => {
  const url = resolveUrl(input);

  if (!url.startsWith(API_BASE_URL)) {
    return nativeFetch(input, init);
  }

  // Root-cause fix (2026-09-20): this used to be `await
  // supabase.auth.getSession()` on EVERY request through here -- a real
  // call into GoTrueClient's internal auth lock, which could hang behind
  // a stuck refresh/network hiccup and stall every single backend request
  // in the app at once. lib/auth-session.ts keeps this cache in sync via
  // onAuthStateChange (including TOKEN_REFRESHED), so this read is
  // synchronous and can never hang.
  const token = getSession()?.access_token;

  if (!token) {
    return nativeFetch(input, init);
  }

  const headers = new Headers(init.headers);
  if (!headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  // Which of the person's companies this request is for (only set when they chose one; the backend validates it)
  const selectedCompany = getSelectedCompanyId();
  if (selectedCompany && !headers.has(COMPANY_HEADER)) headers.set(COMPANY_HEADER, selectedCompany);

  const response = await nativeFetch(input, { ...init, headers });
  const method = input instanceof Request ? input.method : init.method;

  if (response.status === 401) {
    const refreshed = await refreshSessionOnce();
    if (refreshed === 'dead') {
      // Fire-and-forget: the navigation unmounts the caller anyway.
      forceSignOutToLogin();
    } else if (refreshed && !(input instanceof Request)) {
      const retryHeaders = new Headers(init.headers);
      retryHeaders.set('Authorization', `Bearer ${refreshed.access_token}`);
      if (selectedCompany && !retryHeaders.has(COMPANY_HEADER)) retryHeaders.set(COMPANY_HEADER, selectedCompany);
      return softenPermissionDenied(await nativeFetch(input, { ...init, headers: retryHeaders }), method, url);
    }
  }

  // A refused write (403) carries one generic message and is announced to the open form (lib/permission-errors.ts)
  return softenPermissionDenied(response, method, url);
};

export {};
